import React, { useMemo, useState, ReactNode, useRef, useCallback, ChangeEvent, Dispatch, SetStateAction, useEffect, lazy, Suspense } from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    Tabs,
    Tab,
    Typography, Paper,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    Divider,
    Stack,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    ButtonGroup,
    Alert,
    Modal
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { LoadingButton } from '@mui/lab';
import { Localized, useLocalization } from '@fluent/react';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import ChecklistIcon from "@mui/icons-material/Checklist";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DrawIcon from '@mui/icons-material/Draw';
import ErrorIcon from '@mui/icons-material/Error';

import {
    createTestRun,
    deleteTestRun,
    DocumentTest, TestRunParticipant, updateTestRun, TestCriteriaResult, signTestRun
} from '../documentTestsApi';

import { TestPhase, TestPhaseStep } from '../../../testSpecification/testSpecificationApi';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { SectionComments } from '../../SectionComments';
import { docTestsResultsSelectors, docTestsRunsSelectors, docTestsSelectors, showDocComments, changeTestResult, changeTestRun, selectDocument, docDeviationsSelectors } from '../../documentSlice';
import { RichTextEditor } from '../../../../app/RichTextEditor';
import { store } from '../../../../app/store';
import { impossible } from '../../../../app/Utils';
import { DeleteButton } from '../../../../app/components/DeleteButton';
import { APIError, AppTextFieldHandler } from '../../../../app/appTypes';
import produce from "immer";
import { ServerErrorMsg } from '../../../../app/ServerErrorMsg';
import { If } from '../../../../app/If';
import { AssociateDeviationWithTest, CreateDeviationForm } from '../../sections/deviationsSection/CreateDeviationForm';
import { DeviationsLoader } from '../../sections/deviationsSection/DeviationsLoader';
import { DeviationsTable } from '../../sections/deviationsSection/DeviationsTable';
import { PersonData, PersonalDataPicker, defPersonData } from '../../PersonalDataPicker';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { loadSignature, selectSignById } from '../../signaturesSlice';
import { showSuccess } from '../../../notifications/notificationsSlice';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface TestResultDialogProps {
    testId: number;
    documentId: number;
    sectionId: string;
    onSave: (test: DocumentTest) => void;
    onCancel: () => void;
    relatedObjId?: number
    relatedObjType?: string
    headline?: ReactNode
}

const defTester: TestRunParticipant = {
    id: 0,
    runId: 0,
    role: 'TESTER',
    userName: '',
    name: '',
    position: '',
    email: '',
    companyName: '',
    signatureId: null,
}
const defApprover: TestRunParticipant = {
    id: 0,
    runId: 0,
    role: 'APPROVER',
    userName: '',
    name: '',
    position: '',
    email: '',
    companyName: '',
    signatureId: null,
}

export const TestResultDialog = ({
    testId,
    documentId,
    sectionId,
    onSave,
    onCancel,
    relatedObjType,
    relatedObjId,
    headline,
}: TestResultDialogProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const testDefinition = useAppSelector(state => docTestsSelectors.selectById(state, testId)) ?? impossible()
    const run = useAppSelector(state => testDefinition.lastRunId ? docTestsRunsSelectors.selectById(state, testDefinition.lastRunId) : undefined)

    const [fullScreen, setFullScreen] = useState(true);

    const [mainTab, setMainTab] = useState(0);
    const [mode, setMode] = useState<'current' | 'new'>(run === undefined ? 'new' : 'current')

    const spec = testDefinition.testSpecification

    useEffect(() => {
        const persistedTester = run?.participants.find(p => p.role === 'TESTER')
        const persistedApprover = run?.participants.find(p => p.role === 'APPROVER')
        if (persistedTester) {
            setTester(produce(draft => {
                draft.fullName = persistedTester.name
                draft.userName = persistedTester.userName
                draft.position = persistedTester.position
                draft.email = persistedTester.email
                draft.companyName = persistedTester.companyName
            }))
        }
        if (persistedApprover) {
            setApprover(produce(draft => {
                draft.fullName = persistedApprover.name
                draft.userName = persistedApprover.userName
                draft.position = persistedApprover.position
                draft.email = persistedApprover.email
                draft.companyName = persistedApprover.companyName
            }))
        }
    }, [run?.participants])

    const [tester, setTester] = useState<PersonData>(defPersonData)
    const [approver, setApprover] = useState<PersonData>(defPersonData)

    //TODO: legacy states - to delete
    const [testMetodology, setTestMetodology] = useState(spec.metodology ?? '');
    const [saving, setSaving] = useState(false);
    const showComments = useAppSelector(state => showDocComments(state, sectionId))
    const [phaseTab, setPhaseTab] = useState(0);

    const testPhases = spec.testPhases

    const toggleFullScreen = () => setFullScreen((prev) => !prev);
    const handleMainTabChange = (_: React.SyntheticEvent, v: number) => setMainTab(v);
    const handlePhaseTabChange = (_: React.SyntheticEvent, v: number) => setPhaseTab(v);

    const handlePassClick = () => {
        if (run) {
            dispatch(changeTestRun({
                id: run.id,
                changes: {
                    outcome: 'PASSED',
                },
            }))
        }
    }

    const handleFailClick = () => {
        if (run) {
            dispatch(changeTestRun({
                id: run.id,
                changes: {
                    outcome: 'FAILED',
                },
            }))
        }
    }

    const handleCommentChange: AppTextFieldHandler = e => {
        if (run) {
            dispatch(changeTestRun({
                id: run.id,
                changes: {
                    comment: e.target.value,
                },
            }))
        }
    }

    const handleSave = async () => {
        if (run) {
            setSaving(true)
            const persistedTester = run?.participants.find(p => p.role === 'TESTER')
            const persistedApprover = run?.participants.find(p => p.role === 'APPROVER')
            try {
                const testerData: TestRunParticipant = persistedTester ?
                    {...persistedTester
                    , name: tester.fullName
                    , userName: tester.userName ?? ''
                    , position: tester.position
                    , email: tester.email
                    , companyName: tester.companyName
                    } : defTester
                const approverData: TestRunParticipant = persistedApprover ?
                    {...persistedApprover
                    , name: approver.fullName
                    , userName: approver.userName ?? ''
                    , position: approver.position
                    , email: approver.email
                    , companyName: approver.companyName
                    } : defApprover
                await dispatch(updateTestRun({
                    ...run,
                    documentId: documentId.toString(),
                    criteriaResults: docTestsResultsSelectors.selectAll(store.getState()).filter(r => r.runId === run.id),
                    participants: [testerData, approverData],
                })).unwrap()
            } catch (error) {
                console.log(`Saving error: `, error)
           } finally {
                setSaving(false)
            }
        }
    };

    const handleDeleteClick = async () => {
        if (run) {
            setSaving(true)
            try {
                await dispatch(deleteTestRun({
                    runId: run.id,
                    documentId: documentId.toString(),
                })).unwrap()
                setTester(defPersonData)
                setApprover(defPersonData)
            } catch (error) {
                console.log(`Deleting error: `, error)
           } finally {
                setSaving(false)
            }
        }
    };

    const title = <Localized id="results"><span>_Wyniki</span></Localized>


    let content = <></>
    if (run === undefined || mode === 'new') {
        content = <CreateTestRunForm testId={testId} onSuccess={() => setMode('current')} />
    } else if (testPhases.length === 0) {
        content = <Typography variant="body2" color="text.secondary">Brak faz testu do edycji.</Typography>
    } else {
        content = <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1 }}>
            <Box display="flex" flexDirection="column" gap={3} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {headline}
                <TextField
                    label={<Localized id="test-name">_test-name</Localized>}
                    value={spec.name}
                    sx={{ mt: '10px' }}
                    fullWidth
                    disabled={true}
                />

                <Divider />

                <Box sx={{
                    display: 'flex',
                    gap: 4,
                }}>
                    <FormControl component="fieldset" variant="standard" required error={run?.outcome === 'UNKNOWN'}>
                        <FormLabel component="legend">Wynik testu</FormLabel>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={run?.outcome === 'PASSED'} onChange={handlePassClick} name="yes" size='small' />
                                }
                                label="Zaliczony"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox checked={run?.outcome === 'FAILED'} onChange={handleFailClick} name="no" size='small' />
                                }
                                label="Niezaliczony"
                            />
                        </FormGroup>
                        {/* <FormHelperText>Zaznacz tak lub nie</FormHelperText> */}
                    </FormControl>
                    <TextField
                        sx={{
                            flexGrow: 1,
                        }}
                        label={'Komentarz'}
                        value={run?.comment ?? ''}
                        onChange={handleCommentChange}
                        multiline
                        rows={3}
                    />
                </Box>

                <Tabs value={mainTab} onChange={handleMainTabChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={<Localized id="test-procedure">
                        <span>_przebieg testu</span>
                    </Localized>} />
                    <Tab label={<Localized id="description">
                        <span>_description</span>
                    </Localized>} />
                    <Tab label={<Localized id="test-methodology">
                        <span>_metodologia testu</span>
                    </Localized>} />
                    <Tab label={<Localized id="test-participants">
                        <span>_Uczestnicy</span>
                    </Localized>} />
                </Tabs>

                {mainTab === 0 && run && (
                    <>
                        <Tabs value={phaseTab} onChange={handlePhaseTabChange} variant="scrollable" allowScrollButtonsMobile>
                            {testPhases.map((phase) => (
                                <Tab
                                    key={phase.id}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>{phase.phaseType}</span>
                                            {!phase.isActive && (<RemoveCircleOutlineIcon color="error" fontSize="small" />)}
                                            {phase.isActive && (<CheckCircleOutlineIcon color="success" fontSize="small" />)}
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>

                        {testPhases.map((phase, index) =>
                            phaseTab === index
                                ? <TestPhaseResult
                                    key={phase.id}
                                    phase={phase}
                                    runId={run.id}
                                    testId={testId}
                                />
                                : null)}
                    </>
                )}

                {mainTab === 1 && (
                    <Box>
                        <Paper variant="outlined">
                            <Box
                                px={1.5}
                                py={1}
                                bgcolor={(t) => t.palette.grey[100]}
                                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                            >
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Opis testu
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <RichTextEditor
                                    label={l10n.getString("test-description", null, "test-description")}
                                    value={spec.description}
                                    editable={false}
                                    onValueChange={() => {}}
                                />
                            </Box>
                        </Paper>

                    </Box>
                )}
                {mainTab === 2 && (
                    <Box>
                        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                            <Box
                                px={1.5}
                                py={1}
                                bgcolor={(t) => t.palette.grey[100]}
                                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                            >
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Metodologia
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                <RichTextEditor
                                    label={l10n.getString("test-metodology", null, "test-metodology")}
                                    value={testMetodology}
                                    editable={false}
                                    onValueChange={setTestMetodology} />
                            </Box>

                        </Paper>

                    </Box>
                )}
                {mainTab === 3 && <ParticipantsForm
                    documentId={documentId}
                    runId={run.id}
                    testerId={run.participants.find(p => p.role === 'TESTER')?.id ?? 0}
                    testerSigId={run.participants.find(p => p.role === 'TESTER')?.signatureId ?? null}
                    tester={tester}
                    setTester={setTester}
                    approverId={run.participants.find(p => p.role === 'APPROVER')?.id ?? 0}
                    approverSigId={run.participants.find(p => p.role === 'APPROVER')?.signatureId ?? null}
                    approver={approver}
                    setApprover={setApprover}
                />}
            </Box>
        </Box>
    }

    return (
        <Dialog
            open
            fullWidth
            maxWidth={fullScreen ? undefined : 'lg'}
            fullScreen={fullScreen}
            PaperProps={{
                sx: (t) => ({
                    border: `1px solid ${t.palette.divider}`,
                    boxShadow: t.shadows[8],
                    overflow: 'hidden',
                    ...(fullScreen
                        ? { m: 2, height: 'calc(100% - 32px)', borderRadius: 3 }
                        : { borderRadius: 2 }),
                }),
            }}
        >
            <DialogTitle
                sx={(t) => ({
                    bgcolor: t.palette.grey[200],
                    borderBottom: `1px solid ${t.palette.divider}`,
                    py: 1.25,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                })}
            >
                {title}
                <IconButton onClick={toggleFullScreen} style={{ float: 'right' }} color="primary">
                    {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {showComments
                    ? <SectionComments sectionId={sectionId} contextType='test' contextGuid={testDefinition.guid} component={content} />
                    : content}
            </DialogContent>

            <DialogActions>
                <If condition={run !== undefined}>
					{mode === 'current' && <DeleteButton
						loading={saving}
						onClick={handleDeleteClick}
					/>}
                    <LoadingButton
                        loading={saving}
						disabled={mode === 'new'}
                        variant='outlined'
                        startIcon={<AutorenewIcon />}
                        onClick={() => setMode('new')}
                    >
                        Przeprowadź test ponownie
                    </LoadingButton>
                    <LoadingButton
                        loading={saving}
						disabled={mode === 'new'}
                        onClick={handleSave}
                        startIcon={<SaveIcon />}
                        size="small"
                        color="success"
                        variant="contained"
                    >
                        <Localized id="save"><span>_Zapisz</span></Localized>
                    </LoadingButton>
                </If>
                <LoadingButton loading={saving} onClick={onCancel} size="small" variant="outlined">
                    <Localized id="close"><span>_Zamknij</span></Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

interface TestPhaseResultProps {
    phase: TestPhase;
    runId: number
    testId: number
}
const TestPhaseResult = (props: TestPhaseResultProps) => {
    const { phase, runId, testId } = props
  const initialSteps = phase?.steps ?? [];
  const [steps, setSteps] = useState<TestPhaseStep[]>(initialSteps);

  const [selectedStepId, setSelectedStepId] = useState<number | null>(steps[0]?.id ?? null);

  const newStepRef = useRef<HTMLInputElement | null>(null);
  const newStepId = useRef<number | null>(null);

  const selectedStep = useMemo(() => steps.find((s) => s.id === selectedStepId) ?? null, [steps, selectedStepId]);

  const handleStepNameChange = (value: string, stepId: number) => {
    const updated = steps.map((s) => (s.id === stepId ? { ...s, name: value } : s));
    setSteps(updated);
  };

  return (<Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    }}>
    <Paper >
      <Box p={2}>
        {/* <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}> */}
          {/* <TestSubTypeEditor subType={selectedSubType} onUpdatedSubType={handleSubTypeChange} /> */}
        {/* </Box> */}

        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
                <Typography variant="body1" fontWeight={700}>
                  {phase.phaseType} — {<Localized id="test-steps">
                    <span>_test-steps</span>
                  </Localized>} ({steps.length})
                </Typography>
              </Box>
              <Box px={1.5} py={0.5} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={1}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="lp">
                        <span>_Lp.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="name">
                        <span>_name.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="validation-acceptance-criteria">
                        <span>_Kryteria.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={1}></Grid>
                </Grid>
              </Box>

              <List dense disablePadding>
                {steps.map((step) => (
                  <ListItem key={step.id} disableGutters sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                    <ListItemButton
                      selected={selectedStepId === step.id}
                      onClick={() => setSelectedStepId(step.id)}
                      sx={{ py: 1, px: 1.5 }}
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={1}>
                          <Typography variant="body2" color="text.secondary">{step.order}.</Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <ListItemText
                            primary={
                                                            step.name ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    component="div"
                                                                    sx={{
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                        [`& p`]: { m: 0, display: "inline" },
                                                                    }}
                                                                    dangerouslySetInnerHTML={{ __html: step.name }}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" noWrap>
                                                                    <em style={{ color: '#888' }}>
                                                                        <Localized id="test-no-step-description-warning">
                                                                            <span>_Brak opisu…</span>
                                                                        </Localized>
                                                                    </em>
                                                                </Typography>
                                                            )
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Chip size="small" icon={<ChecklistIcon />} label={String(step.criterias.length)} />
                        </Grid>
                        <Grid sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}} item xs={1}>
							<StepResult step={step} runId={runId} />
                        </Grid>
                      </Grid>
                    </ListItemButton>
                  </ListItem>
                ))}
                {steps.length === 0 && (
                  <Box p={2} textAlign="center" color="text.secondary">

                    <Localized id="test-no-steps-warning">
                      <span>_ Brak kroków — dodaj pierwszy.</span>
                    </Localized>
                  </Box>
                )}
              </List>

            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" >
              <Box p={2}>
                {!selectedStep && (
                  <Typography color="text.secondary"
                  >
                    <Localized id="test-choose-step-to-edit">
                      <span>_Wybierz krok po lewej, aby edytować.</span>
                    </Localized>
                  </Typography>
                )}
                {selectedStep && (
                  <Stack spacing={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        noWrap
                        sx={{ width: 80 }}
                      >
                        <Localized id="test-step">
                          <span>_step.</span>
                        </Localized>
                        {selectedStep.order}
                      </Typography>
                                            <Box sx={{ flexGrow: 1 }} ref={newStepId.current === selectedStep.id ? newStepRef : undefined}>
                                                <Typography variant="caption" color="text.secondary">
                                                    <Localized id="test-step-description"></Localized>
                                                </Typography>
                                                {selectedStep.name ? (
                                                    <Typography
                                                        component="div"
                                                        sx={{
                                                            mt: 0.5,
                                                            p: 1,
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            borderRadius: 1,
                                                            bgcolor: "background.paper",
                                                            [`& p`]: { my: 0.5 },
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: selectedStep.name }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ mt: 0.5 }} color="text.secondary" variant="body2">
                                                        <Localized id="test-no-step-description-warning">
                                                            <span>_Brak opisu…</span>
                                                        </Localized>
                                                    </Typography>
                                                )}
                                            </Box>
                    </Box>

                    <Divider />

                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">
                          <Localized id="validation-acceptance-criteria">
                            <span>_Kryteria.</span>
                          </Localized>
                          ({selectedStep.criterias.length})</Typography>
                      </Box>

                      <Stack spacing={1.25}>
                        {selectedStep.criterias.map((c) => (
                          <Box
                            key={c.id}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
                          >
                            <Typography sx={{
                                alignSelf: 'flex-start',
                            }} variant="body2" width={44} textAlign="right" color="text.secondary">
                              {selectedStep.order}.{c.order}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                flexGrow: 1,
                            }}>
                                                                {c.name ? (
                                                                    <Typography
                                                                        component="div"
                                                                        bgcolor={(t) => t.palette.grey[100]}
                                                                        sx={{
                                                                            px: 1,
                                                                            py: 0.5,
                                                                            borderRadius: 0.5,
                                                                            [`& p`]: { my: 0.5 },
                                                                        }}
                                                                        dangerouslySetInnerHTML={{ __html: c.name }}
                                                                    />
                                                                ) : (
                                                                    <Typography bgcolor={(t) => t.palette.grey[100]} sx={{ px: 1 }} color="text.secondary">
                                                                        <Localized id="test-no-criteria-warning">
                                                                            <span>_Brak kryteriów — dodaj pierwsze.</span>
                                                                        </Localized>
                                                                    </Typography>
                                                                )}
                                <Box sx={{
                                    mt: .5,
                                    display: 'flex',
                                    gap: 2,
                                }}>
                                    <TestCriteriaResultWrapper criteriaId={c.id} runId={runId} testId={testId} />
                                </Box>
                            </Box>
                          </Box>
                        ))}
                        {selectedStep.criterias.length === 0 && (
                          <Typography color="text.secondary" variant="body2">
                            <Localized id="test-no-criteria-warning">
                              <span>_Brak kryteriów — dodaj pierwsze.</span>
                            </Localized>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
    <Paper>
        <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
            <Typography variant="body1" fontWeight={700}>Odchylenia</Typography>
        </Box>
        <Box sx={{ p:2 }}>
            <DeviationsLoader>
                <TestDeviations testId={testId} />
            </DeviationsLoader>
        </Box>
    </Paper>
  </Box>);
}

export const TestCriteriaResultWrapper = (props: { criteriaId: number, runId: number, testId: number }) => {
    const { criteriaId, runId, testId } = props

    const resultId = useMemo(() => {
        return docTestsResultsSelectors.selectAll(store.getState()).find(x => x.criteriaId === criteriaId && x.runId === runId)?.id
    }, [criteriaId, runId])

    if (resultId) {
        return <TestCriteriaResultForm resultId={resultId} testId={testId} />
    }

    return <span>Cannot find result entity</span>
}

export const TestCriteriaResultForm = (props: { resultId: number, testId: number }) => {
    const { resultId, testId } = props

    const dispatch = useAppDispatch()

    const result = useAppSelector(state => docTestsResultsSelectors.selectById(state, resultId)) ?? impossible()

    const testDefinition = useAppSelector(state => docTestsSelectors.selectById(state, testId)) ?? impossible()

    const handlePassClick = useCallback(() => {
        dispatch(changeTestResult({
            id: resultId,
            changes: { passed: true },
        }))
        let allPassed = true
        for (const resultId of allTestResultIds(testDefinition, result.runId)) {
            const cr = docTestsResultsSelectors.selectById(store.getState(), resultId)
            if (cr && !cr.passed && cr.id !== resultId) {
                allPassed = false
                break
            }
        }
        if (allPassed) {
            dispatch(changeTestRun({
                id: result.runId,
                changes: {
                    outcome: 'PASSED',
                },
            }))
        }
    }, [])

    const handleFailClick = useCallback(() => {
        dispatch(changeTestResult({
            id: resultId,
            changes: { passed: false },
        }))
        dispatch(changeTestRun({
            id: result.runId,
            changes: {
                outcome: 'FAILED',
            },
        }))
    }, [])

    return <>
        <Box sx={{ flexGrow: 1 }}>
            <RichTextEditor
                label={'Komentarz'}
                value={result.comment ?? ''}
                placeholder={'Dodatkowy komentarz...'}
                editable={true}
                onValueChange={(value) => dispatch(changeTestResult({
                    id: resultId,
                    changes: { comment: value },
                }))}
            />
        </Box>
        <FormControl sx={{ m: 0 }} component="fieldset" variant="standard" required error={!result.passed}>
            <FormLabel component="legend">Spełnia</FormLabel>
            <FormGroup row>
            <FormControlLabel
                control={
                <Checkbox checked={result.passed} onChange={handlePassClick} name="yes" size='small' />
                }
                label="Yes"
            />
            <FormControlLabel
                control={
                <Checkbox checked={!result.passed} onChange={handleFailClick} name="no" size='small' />
                }
                label="No"
            />
            </FormGroup>
            {/* <FormHelperText>Zaznacz tak lub nie</FormHelperText> */}
        </FormControl>
    </>
}

const rememberKey = 'RunTestActionRememberFlag'
const participantsKey = 'RunTestActionDefaultParticipants'

type ParticipantsData =
    { v: '1'
    , testerUserName: string | null
    , testerName: string
    , testerPosition: string
    , testerEmail: string
    , testerCompanyName: string
    , approverUserName: string | null
    , approverName: string
    , approverPosition: string
    , approverEmail: string
    , approverCompanyName: string
    }

function serialize(tester: PersonData, approver: PersonData): string {
    const data: ParticipantsData = {
        v: "1",
        testerUserName: tester.userName,
        testerName: tester.fullName,
        testerPosition: tester.position,
        testerEmail: tester.email,
        testerCompanyName: tester.companyName,
        approverUserName: approver.userName,
        approverName: approver.fullName,
        approverPosition: approver.position,
        approverEmail: approver.email,
        approverCompanyName: approver.companyName,
    }
    return JSON.stringify(data)
}

function deserialize(data: string): { tester: PersonData, approver: PersonData } {
    const obj: ParticipantsData = JSON.parse(data)
    return {
        tester: {
            userName: obj.testerUserName ?? '',
            fullName: obj.testerName,
            position: obj.testerPosition,
            email: obj.testerEmail,
            companyName: obj.testerCompanyName,
        },
        approver: {
            userName: obj.approverUserName ?? '',
            fullName: obj.approverName,
            position: obj.approverPosition,
            email: obj.approverEmail,
            companyName: obj.approverCompanyName,
        },
    }
}

interface CreateTestRunFormProps {
    testId: number
    onSuccess: () => void
}
const CreateTestRunForm = (props: CreateTestRunFormProps) => {
    const { testId, onSuccess } = props

    const dispatch = useAppDispatch()

    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const test = useAppSelector(state => docTestsSelectors.selectById(state, testId)) ?? impossible()

    const [saving, setSaving] = useState(false)
    const [remember, setRemember] = useState(() => {
        const stored = localStorage.getItem(rememberKey)
        if (stored) {
            return JSON.parse(stored)
        } else {
            return false
        }
    })
    const [err, setErr] = useState<APIError | null>(null)
    const [testPerson, setTestPerson] = useState<PersonData>(defPersonData)
    const [approvePerson, setApprovePerson] = useState<PersonData>(defPersonData)

    useEffect(() => {
        const stored = localStorage.getItem(participantsKey)
        if (stored) {
            const { tester: t, approver: a } = deserialize(stored)
            setTestPerson(t)
            setApprovePerson(a)
        }
    }, [])

    const handleRememberClick = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setRemember(e.target.checked)
        localStorage.setItem(rememberKey, JSON.stringify(e.target.checked))
        if (!e.target.checked) {
            localStorage.removeItem(participantsKey)
        }
    }, [])
    const handleStartClick = async () => {
        setSaving(true)

        if (remember) {
            localStorage.setItem(participantsKey, serialize(testPerson, approvePerson))
        } else {
            localStorage.removeItem(participantsKey)
        }

        let result: TestCriteriaResult[] = []
        for (let i = 0; i < test.testSpecification.testPhases.length; i++) {
            const phase = test.testSpecification.testPhases[i];
            for (let j = 0; j < phase.steps.length; j++) {
                const step = phase.steps[j];
                for (let k = 0; k < step.criterias.length; k++) {
                    const criteria = step.criterias[k];
                    result.push({
                        id: 0,
                        runId: 0,
                        criteriaId: criteria.id,
                        value: '',
                        passed: false,
                        comment: '',
                        criteriaNameSnapshot: '',
                    })
                }
            }
        }
        const tester: TestRunParticipant = {
            id: 0,
            runId: 0,
            role: 'TESTER',
            userName: testPerson.userName ?? '',
            name: testPerson.fullName,
            position: testPerson.position,
            email: testPerson.email,
            companyName: testPerson.companyName,
            signatureId: null,
        }
        const approver: TestRunParticipant = {
            id: 0,
            runId: 0,
            role: 'APPROVER',
            userName: approvePerson.userName ?? '',
            name: approvePerson.fullName,
            position: approvePerson.position,
            email: approvePerson.email,
            companyName: approvePerson.companyName,
            signatureId: null,
        }
        try {
            await dispatch(createTestRun({
                id: 0,
                documentTestId: testId,
                attemptNo: 1,
                status: '',
                outcome: 'UNKNOWN',
                executedAt: new Date().toISOString(),
                approvedAt: null,
                comment: '',
                participants: [tester, approver],
                criteriaResults: result,
                documentId: header.id,
            })).unwrap()
            onSuccess()
        } catch (error) {
            setErr(error as APIError)
        } finally {
            setSaving(false)
        }
    }

    // if (test.runs.length > 0) return null

    return <Box>
        <Alert sx={{ my: 1 }} severity='info'>
            Wprowadź osobę przeprowadzjącą test oraz osobę odpowiedzialną za zatwierdzenie.
        </Alert>
        <Grid container spacing={1}>
            <Grid item xs={6}>
                <Paper>
                    <PersonalDataPicker styles={{
                        mt: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }} data={testPerson} setData={setTestPerson} label={<>Tester</>} />
                </Paper>
            </Grid>
            <Grid item xs={6}>
                <Paper>
                    <PersonalDataPicker styles={{
                        mt: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }} data={approvePerson} setData={setApprovePerson} label={<>Zatwierdzający</>} />
                </Paper>
            </Grid>
        </Grid>
        <Grid item xs={12}>
            <FormControlLabel sx={{ mt: 2 }} control={<Checkbox checked={remember} onChange={handleRememberClick} />} label="Zapamiętaj" />
            <ServerErrorMsg err={err} />
        </Grid>
        <Box>
            <LoadingButton loading={saving} onClick={handleStartClick} startIcon={<PlayCircleOutlineIcon />} variant='contained'>Start</LoadingButton>
        </Box>
    </Box>
}

function * allTestResultIds(test: DocumentTest, runId: number) {
    for (let i = 0; i < test.runs.length; i++) {
        const run = test.runs[i];
        if (runId !== run.id) continue
        for (let j = 0; j < run.criteriaResults.length; j++) {
            const cr = run.criteriaResults[j];
            yield cr.id
        }
    }
}

const Signature = lazy(() => import('../../../../app/Signature'))

interface ParticipantsFormProps {
    documentId: number,
    runId: number
    testerId: number
    testerSigId: string | null
    tester: PersonData
    setTester: Dispatch<SetStateAction<PersonData>>
    approverId: number
    approverSigId: string | null
    approver: PersonData
    setApprover: Dispatch<SetStateAction<PersonData>>
}
const ParticipantsForm = (props: ParticipantsFormProps) => {
    const { documentId, runId, tester, testerSigId, testerId, setTester, approver, approverSigId, approverId, setApprover } = props

    const dispatch = useAppDispatch()

    const [signing, setSigning] = useState<'none' | 'tester' | 'approver'>('none')
    const [signature, setSignature] = useState<string | null>(null)
    const [testerSignature, setTesterSignature] = useState<string | null>(null)

    const handleSignTesterClick = () => {
        setSigning('tester')
    }
    const handleSignApproverClick = () => {
        setSigning('approver')
    }
    const handleSaveApproverSignatureClick = useCallback(async (sig: string) => {
        try {
            const sr = await dispatch(signTestRun({
                documentId,
                runId,
                participantId: approverId,
                signature: sig,
                signedAt: null,
            })).unwrap()
            setSignature(sr.signatureId)
            // const s = await dispatch(loadSignature(sr.signatureId)).unwrap()
            dispatch(showSuccess('saved'))
            // console.log('Signature loaded: ', s.sign)
        } catch (error) {
            console.log(error)
        } finally {
            setSigning('none')
        }
    }, [])
    const handleSaveTesterSignatureClick = useCallback(async (sig: string) => {
        try {
            const sr = await dispatch(signTestRun({
                documentId,
                runId,
                participantId: testerId,
                signature: sig,
                signedAt: null,
            })).unwrap()
            setTesterSignature(sr.signatureId)
            // const s = await dispatch(loadSignature(sr.signatureId)).unwrap()
            dispatch(showSuccess('saved'))
            // console.log('Signature loaded: ', s.sign)
        } catch (error) {
            console.log(error)
        } finally {
            setSigning('none')
        }
    }, [])
    const handleCancelSignatureClick = useCallback(() => setSigning('none'), [])

    return <Box sx={{
    }}>
        <Grid container spacing={1}>
            <Grid item xs={6}>
                <Paper>
                    <PersonalDataPicker styles={{
                        mt: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }}
                        data={tester}
                        setData={setTester}
                        label={<>Tester</>}
                        expanded={true}
                    />
                    {testerSigId === null && <Button
                        sx={{ my: 1, ml: 2, }}
                        variant='contained'
                        startIcon={<DrawIcon />}
                        onClick={handleSignTesterClick}
                    >Podpisz</Button>}
                    {testerSigId !== null && <Box sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: .25,
                        [`& img`]: {
                            width: '100%',
                            border: '1px solid var(--gray-400)',
                            borderRadius: 1,
                        },
                    }}>
                        <Typography variant='caption' color='text.secondary'>Podpis:</Typography>
                        <SignaturePreview sigId={testerSigId} />
                    </Box>}
                </Paper>
            </Grid>
            <Grid item xs={6}>
                <Paper>
                    <PersonalDataPicker styles={{
                        mt: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }}
                        data={approver}
                        setData={setApprover}
                        label={<>Zatwierdzający</>}
                        expanded={true}
                        readonly={signature !== null}
                    />
                    {approverSigId === null && <Button
                        sx={{ my: 1, ml: 2, }}
                        variant='contained'
                        startIcon={<DrawIcon />}
                        onClick={handleSignApproverClick}
                    >Podpisz</Button>}
                    {approverSigId !== null && <Box sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: .25,
                        [`& img`]: {
                            width: '100%',
                            border: '1px solid var(--gray-400)',
                            borderRadius: 1,
                        },
                    }}>
                        <Typography variant='caption' color='text.secondary'>Podpis:</Typography>
                        <SignaturePreview sigId={approverSigId} />
                    </Box>}
                </Paper>
            </Grid>
        </Grid>
        <Modal open={signing !== 'none'}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 900,
                bgcolor: 'background.paper',
                border: '1px solid #000',
                boxShadow: 24,
                p: 2,
                [`& canvas`]: {
                    border: '1px solid var(--gray-500)',
                    borderRadius: 1,
                },
            }}>
                <Typography variant='body1'><b>{signing === 'approver' ? approver.fullName : tester.fullName}</b></Typography>
                <Typography variant='caption' color='text.secondary'>Podpis:</Typography>
                <Suspense fallback={<span>Inicjalizacja...</span>}>
                    <Signature
                        save={signing === 'approver' ? handleSaveApproverSignatureClick : handleSaveTesterSignatureClick}
                        cancel={handleCancelSignatureClick}
                    />
                </Suspense>
            </Box>
        </Modal>
    </Box>
}

interface TestDeviationsProps {
    testId: number
}
const TestDeviations = (props: TestDeviationsProps) => {
    const { testId } = props

    const deviations = useAppSelector(state => docDeviationsSelectors.selectAll(state).filter(d => d.tests.findIndex(t => t.documentTestId === testId) >= 0))

    const [mode, setMode] = useState<'view' | 'form' | 'assoc'>('view')

    let content: ReactNode = <></>
    switch (mode) {
        case 'view':
            content = <ButtonGroup>
                <Button size='small' variant='outlined' onClick={() => setMode('form')}>Utwórz odchylenie</Button>
                <Button size='small' variant='outlined' onClick={() => setMode('assoc')}>Skojarz z odchyleniem</Button>
            </ButtonGroup>
            break;
        case 'form':
            content = <CreateDeviationForm id={undefined} onSaved={() => setMode('view')} onCancel={() => setMode('view')} testId={testId} />
            break;
        case 'assoc':
            content = <AssociateDeviationWithTest id={undefined} onSaved={() => setMode('view')} onCancel={() => setMode('view')} testId={testId} />
            break;
    }

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    }}>
        <DeviationsTable items={deviations}></DeviationsTable>
        <Box>
            {content}
        </Box>
    </Box>
}

export const SignaturePreview = (props: { sigId: string }) => {
    const dispatch = useAppDispatch()

    const sig = useAppSelector(state => selectSignById(state.signatures, props.sigId))

    useEffect(() => {
        if (props.sigId && !sig) {
            dispatch(loadSignature(props.sigId)).unwrap()
        }
    }, [])

    if (sig) {
        return <img src={sig.sign} />
    } else {
        return <></>
    }
}

const StepResult = (props: { step: TestPhaseStep, runId: number }) => {
	const { step, runId } = props
	
	const passed = step.criterias.every(crit => {
        const result = docTestsResultsSelectors.selectAll(store.getState()).find(x => x.criteriaId === crit.id && x.runId === runId)
		if (result) {
			return result.passed
		} else return false
	})

	if (passed) {
		return <>
			<CheckIcon color='success' />
			{/* <ErrorIcon color='error' /> */}
		</>
	} else {
		return <CloseIcon color='error' />
	}
}
